import { InformationCircleIcon } from "@heroicons/react/24/outline"

import { Tag, Tooltip, TableColumnsType } from "antd"
import { formatDateReadable, formatTimeReadble } from "utils/formatDate"
import { formatCurrencyNoSymbol } from "utils/currency"
import ViewTransferDetails from "../ViewTransferDetails"
import { useState } from "react"
import { availableFlags } from "utils/flags"
import { AppTable, TableActionSelect } from "components/new-stuff/app-table"
import { TransactionData } from "../interface"

interface props {
  transactionsData: TransactionData[] | undefined
  loading: boolean
}

const TransfersTable = ({ transactionsData, loading }: props) => {
  const [activeReference, setActiveReference] = useState("")
  const [showDetails, setShowDetails] = useState(false)

  const availableCurrencies = [
    ...new Set(
      transactionsData?.map((item) => item?.meta?.destinationCurrency)
    ),
  ].filter((code): code is string => Boolean(code))

  const TableColumns: TableColumnsType<TransactionData> = [
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (_, record) => (
        <div className="whitespace-nowrap flex flex-col ">
          <span className="text-padeBlack font-circular text-sm">
            {formatDateReadable(String(record?.createdAt))},
          </span>
          <span className="font-avenir text-xs text-neutral300">
            {formatTimeReadble(String(record?.createdAt))}
          </span>
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (_, record) => {
        const isLocal = record.currency === "NGN" || record.currency === "GBP"
        const currencyCode = isLocal
          ? record.currency
          : record.meta?.destinationCurrency
        const currencyInfo = availableFlags.find(
          (flag) => flag.code === currencyCode
        )
        const usdSymbol = availableFlags?.find(
          (flag) => flag.code === "USD"
        )?.symbol
        const value = isLocal ? record?.amount : record.meta?.receivingAmount
        const valueInUSD =
          !isLocal &&
          record?.meta?.destinationCurrency !== "USD" &&
          record.amount

        return (
          <div className="text-sm flex-nowrap">
            <p className="text-padeBlack font-circular text-sm">
              {currencyInfo?.symbol}
              {formatCurrencyNoSymbol(Number(value))}
            </p>
            {valueInUSD && (
              <span className="font-avenir text-xs text-neutral300 flex`">
                ~ {usdSymbol}
                {formatCurrencyNoSymbol(Number(valueInUSD))}
              </span>
            )}
          </div>
        )
      },
    },
    {
      title: "Charge",
      dataIndex: "fee",
      render: (_, record) => {
        const currencyCode =
          record.currency === "NGN" || record.currency === "GBP"
            ? record.currency
            : record.meta?.destinationCurrency
        const currencyInfo = availableFlags.find(
          (flag) => flag.code === currencyCode
        )
        const usdSymbol = availableFlags?.find(
          (flag) => flag.code === "USD"
        )?.symbol
        const symbol =
          record.currency === "NGN" ? currencyInfo?.symbol : usdSymbol

        return (
          <div className="text-padeBlack font-circular text-sm capitalize">
            {record.fee ? (
              <p>
                {symbol}
                {formatCurrencyNoSymbol(Number(record.fee))}
              </p>
            ) : (
              "-"
            )}
          </div>
        )
      },
    },
    {
      title: "Currency",
      dataIndex: "currency",
      filters: availableCurrencies.map((code) => ({
        text: code,
        value: code,
      })),
      onFilter: (value, record) => {
        const currencyCode =
          record.currency === "NGN" || record.currency === "GBP"
            ? record.currency
            : record.meta?.destinationCurrency
        return currencyCode === value
      },
      render: (_, record) => {
        const isLocal = record.currency === "NGN" || record.currency === "GBP"
        const currencyCode = isLocal
          ? record.currency
          : record.meta?.destinationCurrency
        const currencyInfo = availableFlags.find(
          (flag) => flag.code === currencyCode
        )

        return (
          <div className="flex items-center gap-2">
            <div>
              <img
                src={currencyInfo?.flag}
                alt=""
                className="h-6 w-6"
              />
            </div>
            <span className="font-circular flex flex-wrap text-sm text-padeBlack">
              {currencyCode}
            </span>
          </div>
        )
      },
    },
    {
      title: "Recipient",
      dataIndex: "accountName",
      render: (_, record) => {
        const recipient =
          record.currency === "NGN"
            ? record?.accountName
            : record?.beneficiaryType === "company"
            ? `${record?.beneficiaryCompanyName}`
            : `${record.beneficiaryFirstName} ${record?.beneficiaryLastName}` ||
              "-"

        return (
          <div className="text-padeBlack font-circular text-sm">
            <p>{recipient}</p>
          </div>
        )
      },
    },
    {
      title: "Account",
      dataIndex: "bankCode",
      render: (_, record) => (
        <div className="text-padeBlack font-circular text-sm">
          <p>{record.bankCode || "-"}</p>
          <span className="text-xs break-all">{record.accountNumber}</span>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "product",
      render: (_, record) => (
        <div className="text-padeBlack font-circular text-sm capitalize">
          <p>{record.product || "-"}</p>
        </div>
      ),
    },
    {
      title: "Reference",
      dataIndex: "reference",
      render: (_, record) => (
        <div className="text-padeBlack font-circular text-sm overflow-hidden">
          <p>{record.reference}</p>
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (_, record) => (
        <div className="text-padeBlack font-circular text-sm overflow-hidden">
          <p>{record.description}</p>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, record) => (
        <div className="flex items-center ">
          <Tag
            color={
              record.status === "successful"
                ? "success"
                : record.status === "failed"
                ? "error"
                : record?.status === "processing"
                ? "warning"
                : "default"
            }
          >
            {record.status}
          </Tag>
          {record.status === "failed" && (
            <Tooltip
              title={record?.statusMessage}
              className="cursor-pointer"
            >
              <InformationCircleIcon className="h-4 w-4" />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "",
      dataIndex: "reference",
      key: "action",
      render: (_, record) => (
        <TableActionSelect
          items={[
            {
              key: "view",
              label: "View",
              onClick: () => {
                setActiveReference(record.reference)
                setShowDetails(true)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <AppTable
        dataSource={transactionsData || []}
        columns={TableColumns}
        rowKey="_id"
        loading={loading}
        className="text-[#42526D]"
      />
      {activeReference && (
        <ViewTransferDetails
          reference={activeReference}
          onClose={() => {
            setActiveReference("")
            setShowDetails(false)
          }}
          open={showDetails}
        />
      )}
    </>
  )
}
export default TransfersTable
